import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import { useSelector, useDispatch } from 'react-redux';

import { QuestionWrapper, Tooltip, Typography } from 'common/components';
import { CompanyInfo } from 'common/forms';
import { CompanyInfoFormValues } from 'common/models/Forms';

import { QuestionTitle, PageControls } from '../../../../components';
import { selectInformationsCompanyInfo, setInformationsCompanyInfoValuesAction } from '../../../../ducks';
import { selectPartnerData } from '../../../../../auth/ducks';
import { useFormStepContinueRef } from '../../../../hooks/useFormStepContinueRef';
import { FeatureProps } from '../../../../index';
import { Col, Row } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

export default function CompanyInfoFeature({ isPrivileged }: FeatureProps) {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialValues = useSelector(selectInformationsCompanyInfo);
  const userValues = useSelector(selectPartnerData);

  const { bindFormik, continueWithSoftValidation, showSoftContinueHint } =
    useFormStepContinueRef<CompanyInfoFormValues>();

  function handleBackClick() {
    navigate(-1);
  }

  async function handleContinueClick() {
    await continueWithSoftValidation((values) => {
      handleSubmit(values);
    });
  }

  function bindSubmit(formik: FormikProps<CompanyInfoFormValues>) {
    bindFormik(formik);
  }

  function handleSubmit(values: CompanyInfoFormValues) {
    if (!isPrivileged) {
      dispatch(setInformationsCompanyInfoValuesAction(values));
    }
    navigate(`/form/${id}/informations/customer-info-1`);
  }

  function renderCompanyInfoTooltip() {
    return (
      i18n.language === 'cz' && (
        <Tooltip
          title={
            <div className="company-info-tooltip">
              <Typography.Paragraph>
                {t('informationsFeature.companyInfoTooltip1')}
                <br />
              </Typography.Paragraph>
              <Typography.Paragraph>{t('informationsFeature.companyInfoTooltip2')}</Typography.Paragraph>
              <Typography.Paragraph>{t('informationsFeature.companyInfoTooltip3')}</Typography.Paragraph>
              <Typography.Paragraph>{t('informationsFeature.companyInfoTooltip4')}</Typography.Paragraph>
            </div>
          }
        >
          <InfoCircleOutlined />
        </Tooltip>
      )
    );
  }

  return (
    <>
      <QuestionTitle>
        <Row justify="space-between">
          <Col span={23}>{t('informationsFeature.companyInfo')}</Col>
          <Col>{renderCompanyInfoTooltip()}</Col>
        </Row>
      </QuestionTitle>
      <QuestionWrapper>
        <CompanyInfo
          initialValues={initialValues || userValues || undefined}
          onSubmit={handleSubmit}
          bindSubmit={bindSubmit}
        />
      </QuestionWrapper>
      <PageControls softContinueHint={showSoftContinueHint} onBack={handleBackClick} onContinue={handleContinueClick} />
    </>
  );
}
